import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  loginService,
  logoutService,
  getToken,
  getUser,
  updateUser,
} from "services/auth.service";
import {
  clearAuthorizationCache,
  loadAuthorization,
} from "auth/authorization.service";
import { userLoginHistoryService } from "modules/systems/services/userLoginHistory.service";
import { getLoginInfo } from "utils/deviceInfo";

const AuthContext = createContext();

// Tạo BroadcastChannel để đồng bộ giữa các tab
const authChannel = new BroadcastChannel("auth_channel");

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentLoginHistoryId, setCurrentLoginHistoryId] = useState(null);
  const navigate = useNavigate();

  // Kiểm tra trạng thái authentication từ token
  const checkAuth = (shouldBroadcast = false) => {

    const token = getToken();
    const userData = getUser();
    const newIsAuthenticated = !!token;



    setIsAuthenticated(newIsAuthenticated);
    setUser(userData);
    setIsLoading(false);



    // Chỉ broadcast khi được yêu cầu (ví dụ: sau khi login/logout thành công)
    if (shouldBroadcast) {
      authChannel.postMessage({
        type: "AUTH_STATE_CHANGE",
        isAuthenticated: newIsAuthenticated,
        user: userData,
      });
    }
  };

  // Khởi tạo trạng thái auth khi component mount
  useEffect(() => {


    const initAuth = () => {
      try {
        checkAuth(false); // Không broadcast khi khởi tạo

        // Khôi phục loginHistoryId từ localStorage nếu có
        const savedLoginHistoryId = localStorage.getItem(
          "currentLoginHistoryId"
        );
        if (savedLoginHistoryId) {
          setCurrentLoginHistoryId(parseInt(savedLoginHistoryId));
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        setIsLoading(false); // Đảm bảo không bị stuck
      }
    };

    // Thực hiện khởi tạo
    initAuth();

    // Timeout backup để đảm bảo không bị stuck
    const timeout = setTimeout(() => {

      setIsLoading(false);
    }, 3000);

    // Lắng nghe sự thay đổi từ các tab khác
    const handleAuthMessage = (event) => {
      if (event.data.type === "AUTH_STATE_CHANGE") {
        setIsAuthenticated(event.data.isAuthenticated);
        setUser(event.data.user);
        if (event.data.isAuthenticated) {
          loadAuthorization({ force: true }).catch(() => {});
        } else {
          clearAuthorizationCache();
        }
      }
    };
    authChannel.addEventListener("message", handleAuthMessage);

    // Xử lý khi người dùng đóng browser/tab
    const handleBeforeUnload = async () => {
      const loginHistoryId = localStorage.getItem("currentLoginHistoryId");
      const userData = getUser();

      if (loginHistoryId && userData) {
        try {
          // Tạo thời gian địa phương theo format YYYY-MM-DDTHH:mm:ss
          const now = new Date();
          const localDateTime = new Date(
            now.getTime() - now.getTimezoneOffset() * 60000
          )
            .toISOString()
            .slice(0, 19);

          const logoutData = {
            logoutTime: localDateTime,
            updateBy: userData.userName,
          };

          // Sử dụng sendBeacon để đảm bảo request được gửi khi đóng browser
          const blob = new Blob([JSON.stringify(logoutData)], {
            type: "application/json",
          });
          navigator.sendBeacon(
            `${process.env.REACT_APP_API_URL}/api/userloginhistory/${loginHistoryId}/logout`,
            blob
          );
        } catch (error) {
          console.error("Error updating logout time on page unload:", error);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup
    return () => {
      clearTimeout(timeout);
      authChannel.removeEventListener("message", handleAuthMessage);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const login = async (username, password, rememberMe) => {
    try {
      const data = await loginService(username, password, rememberMe);

      // Kiểm tra và broadcast sau khi login thành công
      checkAuth(true);
      await loadAuthorization({ force: true }).catch(() => {});

      // Lấy thông tin user sau khi login thành công
      const userData = getUser();

      if (userData && userData.userId) {
        try {
          // Thu thập thông tin device và browser
          const loginInfo = await getLoginInfo(
            userData.userId,
            userData.userName
          );

          // Gửi thông tin login history đến backend
          const loginHistoryResponse =
            await userLoginHistoryService.createUserLoginHistory(loginInfo);

          if (loginHistoryResponse.success && loginHistoryResponse.data) {
            setCurrentLoginHistoryId(loginHistoryResponse.data);
            // Lưu loginHistoryId vào localStorage để có thể cập nhật logout time
            localStorage.setItem(
              "currentLoginHistoryId",
              loginHistoryResponse.data.toString()
            );
          } else {
            console.error(
              "Failed to save login history:",
              loginHistoryResponse
            );
          }
        } catch (error) {
          console.error("Error saving login history:", error);
          // Không throw error vì login đã thành công, chỉ log lỗi
        }
      } else {
      }
      return data;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Cập nhật thời gian logout nếu có loginHistoryId
      const loginHistoryId =
        currentLoginHistoryId || localStorage.getItem("currentLoginHistoryId");
      const userData = getUser();

      if (loginHistoryId && userData) {
        try {
          // Tạo thời gian địa phương theo format YYYY-MM-DDTHH:mm:ss
          const now = new Date();
          const localDateTime = new Date(
            now.getTime() - now.getTimezoneOffset() * 60000
          )
            .toISOString()
            .slice(0, 19);

          const logoutData = {
            logoutTime: localDateTime,
            updateBy: userData.userName,
          };

          await userLoginHistoryService.updateLogoutTime(
            parseInt(loginHistoryId),
            logoutData
          );
        } catch (error) {
          console.error("Error updating logout time:", error);
          // Không throw error vì logout vẫn cần tiếp tục
        }
      } else {
      }

      // Xóa loginHistoryId khỏi localStorage
      localStorage.removeItem("currentLoginHistoryId");
      // Xóa sidebar state để login lần sau bắt đầu với menu đóng
      localStorage.removeItem("sidebar-expanded-groups");
      setCurrentLoginHistoryId(null);
    } catch (error) {
      console.error("Error during logout process:", error);
    }

    // Thực hiện logout service
    logoutService();
    setIsAuthenticated(false);
    setUser(null);

    // Broadcast logout cho các tab khác
    authChannel.postMessage({
      type: "AUTH_STATE_CHANGE",
      isAuthenticated: false,
      user: null,
    });

    // Redirect về trang login
    navigate("/login", { replace: true });
  };

  // Method để cập nhật thông tin user
  const updateUserContext = (newUserData) => {
    setUser(newUserData);
    // Cập nhật storage bằng auth.service
    updateUser(newUserData);

    // Broadcast cho các tab khác
    authChannel.postMessage({
      type: "AUTH_STATE_CHANGE",
      isAuthenticated: true,
      user: newUserData,
    });
  };
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div>Đang tải...</div>
        <div style={{ fontSize: "12px", color: "#666" }}>Khởi tạo ứng dụng</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, user, login, logout, updateUserContext }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
